import { html } from 'lit';
import type { LandingContent } from '../../shared/content/landingContent.js';
import { companyHeroCopy } from '../../shared/content/companyHomeContent.js';
import { highlightedHeading } from '../components/highlightedHeading.js';
import { editorSemanticText } from '../editor/editorSemanticText.js';
import {
  editorNodeClass,
  editorNodeClick,
  editorNodePointerDown,
  editorNodeStyle,
  type LandingEditorRenderOptions,
} from '../editor/landingEditorRender.js';
import { openCompanyContactDialog } from './navigation.js';
import './companyPeopleHeroElement.js';

// The portrait perimeter leaves a clear centre for the two-line slogan.
// Stable slots make SSR, resizing and the editor agree; identities never shuffle.
const slots = [
  [7, 19, 128],
  [22, 12, 100],
  [39, 9, 76],
  [60, 10, 96],
  [78, 12, 112],
  [94, 18, 140],
  [1, 43, 96],
  [7, 35, 154],
  [93, 35, 150],
  [99, 47, 88],
  [7, 66, 166],
  [24, 60, 94],
  [78, 60, 104],
  [94, 68, 160],
  [17, 85, 112],
  [35, 87, 144],
  [57, 88, 108],
  [78, 87, 132],
  [1, 96, 78],
  [99, 96, 84],
  [31, 23, 66],
  [69, 23, 72],
  [43, 73, 68],
  [65, 73, 78],
];
const factIcons = [
  'icon-[material-symbols--person-check]',
  'icon-[material-symbols--handshake]',
  'icon-[material-symbols--support-agent]',
];
// A balanced, quieter selection for the phone composition. All 24 remain editable.
const phonePeople = new Set([0, 1, 3, 4, 6, 8, 11, 12, 17, 23]);

export const companyPeopleHeroSection = (content: LandingContent, editor?: LandingEditorRenderOptions) => {
  const hero = content.companyPeopleHero;
  // The two-line headline gets a wider stage; supporting copy keeps its own scale.
  const fixedTitleLines = hero.title === companyHeroCopy.de.title;
  const key = 'companyPeopleHero';
  const enesId = `${key}.portrait.image`;
  const hasPortrait = Boolean(hero.portrait.image) && !editor?.project.elementFrames[enesId]?.hidden;
  const hasFacts =
    !editor?.project.elementFrames[`${key}.benefits`]?.hidden &&
    hero.benefits.some((_, index) => !editor?.project.elementFrames[`${key}.benefits.${index}`]?.hidden);
  const text = (field: string, value: string, tag: 'p' | 'h2' = 'p', className = '') =>
    editorSemanticText({ editor, elementId: `${key}.${field}`, fallbackTag: tag, className, content: value });
  const imagePointer = (event: PointerEvent, id: string) => {
    if (editor?.mode !== 'editor') return;
    event.preventDefault();
    event.stopPropagation();
    editor.onElementSelect?.(id);
    editor.onElementPointerDown?.(event, id);
  };
  const action = (event: MouseEvent, field: 'introAction' | 'primaryAction' | 'secondaryAction', image = false) => {
    if (editor?.mode === 'editor') {
      event.preventDefault();
      event.stopPropagation();
      editorNodeClick(editor, image ? enesId : `${key}.${field}.label`, event);
      return;
    }
    if (hero[field].href === '#kontakt') {
      event.preventDefault();
      openCompanyContactDialog((event.currentTarget as HTMLElement).closest('.vp-page'));
    } else if (hero[field].href === '#matching-demo') {
      const demo = (event.currentTarget as HTMLElement)
        .closest('.vp-page')
        ?.querySelector('vp-company-platform-hero');
      if (demo) {
        event.preventDefault();
        demo.dispatchEvent(new CustomEvent('open-matching-demo', { detail: { instant: event.detail === 0 } }));
      }
    } else if (image) {
      window.location.assign(hero[field].href);
    }
  };
  return html`
    <section
      class=${editorNodeClass(editor, `section.${key}`, 'vp-people-hero')}
      style=${editorNodeStyle(editor, `section.${key}`)}
      @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `section.${key}`, event)}
      @click=${(event: MouseEvent) => editorNodeClick(editor, `section.${key}`, event)}
    >
      <vp-company-people-hero data-motion=${hero.motion} data-editor=${editor?.mode === 'editor' ? 'true' : 'false'}>
        <div class="vp-people-scene">
          <div class="vp-people-stage">
            <!-- Radial depth applies only to the decorative portraits, keeping content crisp. -->
            <div
              class=${editorNodeClass(editor, `${key}.people`, 'vp-people-bubbles')}
              style=${editorNodeStyle(editor, `${key}.people`)}
              aria-hidden="true"
            >
              ${hero.people.map((person, index) => {
                const id = `${key}.people.${index}`;
                if (
                  !person.image ||
                  editor?.project.elementFrames[`${id}.image`]?.hidden ||
                  editor?.project.elementFrames[id]?.hidden
                )
                  return '';
                const slot = slots[index % slots.length]!;
                return html`<span
                  class=${editorNodeClass(editor, id, 'vp-people-bubble')}
                  data-phone-person=${phonePeople.has(index) ? 'true' : 'false'}
                  data-phone-featured=${index === 1 ? 'true' : 'false'}
                  style=${`--person-x:${slot[0]}%;--person-y:${slot[1]}%;--person-size:${slot[2]}px;--person-mobile-size:${58 + (index % 3) * 12}px;--person-flow-x:${((index % 6) - 2.5) * 4}px;--person-flow-y:${(Math.floor(index / 6) - 1.5) * 6}px;${editorNodeStyle(editor, id)}`}
                  @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, id, event)}
                  @click=${(event: MouseEvent) => editorNodeClick(editor, id, event)}
                >
                  <img
                    src=${person.image}
                    alt=${person.imageAlt}
                    width="512"
                    height="512"
                    loading="eager"
                    decoding="async"
                    class=${editorNodeClass(editor, `${id}.image`, 'vp-people-face')}
                    style=${editorNodeStyle(editor, `${id}.image`)}
                    @pointerdown=${(event: PointerEvent) => imagePointer(event, `${id}.image`)}
                    @click=${(event: MouseEvent) => {
                      if (editor?.mode === 'editor') {
                        event.stopPropagation();
                        editorNodeClick(editor, `${id}.image`, event);
                      }
                    }}
                  />
                </span>`;
              })}
            </div>
            <div class="vp-people-center">
              <div
                class=${editorNodeClass(editor, `${key}.copy`, 'vp-people-copy vp-people-panel')}
                style=${editorNodeStyle(editor, `${key}.copy`)}
                data-story-panel="intro"
              >
                <div class="vp-people-panel-content">
                  ${highlightedHeading({
                    title: fixedTitleLines ? hero.title.replace('. ', '.\n') : hero.title,
                    highlight: hero.highlight,
                    className: `vp-people-title${fixedTitleLines ? ' vp-people-title--two-lines' : ''}`,
                    editor,
                    titleElementId: `${key}.title`,
                    highlightElementId: `${key}.highlight`,
                  })}
                  ${text('intro', hero.intro, 'p', 'vp-people-intro')}
                  <a
                    href=${hero.introAction.href}
                    class=${editorNodeClass(editor, `${key}.introAction.label`, 'vp-action vp-action-primary vp-people-intro-action')}
                    style=${editorNodeStyle(editor, `${key}.introAction.label`)}
                    @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${key}.introAction.label`, event)}
                    @click=${(event: MouseEvent) => action(event, 'introAction')}
                  >
                    ${hero.introAction.label}
                  </a>
                </div>
              </div>
              ${hasFacts
                ? html` <div class="vp-people-facts-panel vp-people-panel" data-story-panel="facts">
                    <div class="vp-people-panel-content vp-people-facts-content">
                      <ul
                        id="company-people-facts"
                        class=${editorNodeClass(editor, `${key}.benefits`, 'vp-people-facts')}
                        style=${editorNodeStyle(editor, `${key}.benefits`)}
                      >
                        ${hero.benefits.map(
                          (benefit, index) =>
                            html` <li
                              class=${editorNodeClass(editor, `${key}.benefits.${index}`, 'vp-people-fact')}
                              style=${`--fact-shift:${8 + (index % 3) * 5}px;--fact-tilt:${[-8, 7, -5][index % 3]}deg;${editorNodeStyle(editor, `${key}.benefits.${index}`)}`}
                            >
                              <span class="vp-people-fact-mark" aria-hidden="true">
                                <span class=${factIcons[index % factIcons.length]}></span>
                              </span>
                              <div class="vp-people-fact-copy">
                                ${text(`benefits.${index}.title`, benefit.title, 'h2')}
                                ${text(`benefits.${index}.text`, benefit.text, 'p', 'vp-people-fact-statement')}
                              </div>
                            </li>`,
                        )}
                      </ul>
                    </div>
                  </div>`
                : ''}
              <div class="vp-people-contact-panel vp-people-panel" data-story-panel="contact">
                <div class="vp-people-panel-content vp-people-contact-content">
                  <div
                    id="company-people-contact"
                    tabindex="-1"
                    class=${editorNodeClass(
                      editor,
                      `${key}.contact`,
                      `vp-people-contact${hasPortrait ? '' : ' is-without-portrait'}`,
                    )}
                    style=${editorNodeStyle(editor, `${key}.contact`)}
                  >
                    ${hasPortrait
                      ? html`<button
                          type="button"
                          class="vp-people-enes-button"
                          aria-label=${hero.primaryAction.label}
                          @pointerdown=${(event: PointerEvent) => imagePointer(event, enesId)}
                          @click=${(event: MouseEvent) => action(event, 'primaryAction', true)}
                        >
                          <img
                            src=${hero.portrait.image}
                            alt=${hero.portrait.imageAlt}
                            width="400"
                            height="500"
                            loading="eager"
                            decoding="async"
                            class=${editorNodeClass(editor, enesId, 'vp-people-enes')}
                            style=${editorNodeStyle(editor, enesId)}
                          />
                        </button>`
                      : ''}
                    <div
                      class=${editorNodeClass(editor, `${key}.actions`, 'vp-people-actions')}
                      style=${editorNodeStyle(editor, `${key}.actions`)}
                    >
                      ${(['secondaryAction', 'primaryAction'] as const).map(
                        field =>
                          html`<a
                            href=${hero[field].href}
                            class=${`vp-people-speech vp-people-speech--${field}`}
                            data-people-action=${field}
                            @pointerdown=${(event: PointerEvent) =>
                              editorNodePointerDown(editor, `${key}.${field}.label`, event)}
                            @click=${(event: MouseEvent) => action(event, field)}
                          >
                            ${text(`${field}.label`, hero[field].label, 'p', 'vp-people-speech-label')}<span
                              aria-hidden="true"
                              >→</span
                            >
                          </a>`,
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <a
              class=${editorNodeClass(editor, `${key}.scrollLabel`, 'vp-people-scroll')}
              style=${editorNodeStyle(editor, `${key}.scrollLabel`)}
              href="#company-people-facts"
              aria-label=${hero.scrollLabel}
              @click=${(event: MouseEvent) => {
                event.preventDefault();
                if (editor?.mode === 'editor') {
                  editorNodeClick(editor, `${key}.scrollLabel`, event);
                  return;
                }
                (event.currentTarget as HTMLElement)
                  .closest('vp-company-people-hero')
                  ?.dispatchEvent(new CustomEvent('show-people-next'));
              }}
            >
              ${[0, 1, 2].map(
                () =>
                  html`<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="m6 9 6 6 6-6" /></svg>`,
              )}
            </a>
          </div>
        </div>
      </vp-company-people-hero>
    </section>
  `;
};
